// ══════════════════════════════════════════════════════════════════
//  ED CRM - Agregação canônica de comissões (FONTE ÚNICA)
// ══════════════════════════════════════════════════════════════════
//  computePersonTotals é a ÚNICA função que agrega comissões por pessoa.
//  Consumida por:
//    - CommissionsPage  → ranking por pessoa + card consolidado
//    - MyPanel/CommissionsRanking → ranking de comissões
//  Assim individual, ranking e consolidado nunca divergem.
//
//  Regras (alinhadas ao banco — Config/62):
//   • Base: soma closer_commission (lado closer) e sdr_commission (lado sdr,
//     só quando há SDR distinto — !closer_as_sdr). Os valores já vêm
//     normalizados por commissionService.normalizeCardOwners; o turbo já
//     está embutido APENAS no closer_commission (SDR nunca tem turbo).
//   • Bônus: calculado POR PESSOA, por lado (escala closer_bonus / sdr_bonus),
//     sobre o faturamento atribuído àquela pessoa naquele lado.
//     NUNCA sobre o faturamento agregado do time (pool).
//   • total = comissaoBase + bonus.
// ══════════════════════════════════════════════════════════════════

import { DEFAULT_RATES, getBonusForFaturamento } from './commissionHelpers';
import type { CommissionRates } from '@/types/domain';

export interface CommissionPerson {
  id: string;
  nome?: string | null;
  initials?: string | null;
  color?: string | null;
  role?: string | null;
}

export interface NormalizedCommission {
  faturamento?: number | string | null;
  closer?: CommissionPerson | null;
  sdr?: CommissionPerson | null;
  closer_commission?: number | string | null;
  sdr_commission?: number | string | null;
  closer_as_sdr?: boolean | null;
}

export interface SideTotals {
  vendas: number;
  faturamento: number;
  base: number;
  bonus: number;
}

interface PersonAccumulator {
  id: string;
  nome: string;
  initials?: string | null;
  color?: string | null;
  role: string;
  closer: SideTotals;
  sdr: SideTotals;
}

export interface PersonTotals extends PersonAccumulator {
  vendas: number;
  faturamento: number;
  comissaoBase: number;
  bonus: number;
  comissao: number;
  total: number;
}

// commissions: lista já normalizada (listCommissions).
// rates: config.commission_rates (ou DEFAULT_RATES).
// seedUsers: usuários conhecidos {id, nome, initials, color, role} para
//            aparecerem no ranking mesmo zerados (opcional).
export const computePersonTotals = (
  commissions: NormalizedCommission[] = [],
  rates: CommissionRates | null = null,
  seedUsers: CommissionPerson[] = [],
): PersonTotals[] => {
  const safeRates = rates || DEFAULT_RATES;
  const byUser = new Map<string, PersonAccumulator>();

  const ensure = (user: CommissionPerson | null | undefined, roleLabel: string): PersonAccumulator | null => {
    if (!user?.id) return null;
    if (!byUser.has(user.id)) {
      byUser.set(user.id, {
        id: user.id,
        nome: user.nome || 'Usuário',
        initials: user.initials,
        color: user.color,
        role: roleLabel,                          // papel de exibição (primeiro visto)
        closer: { vendas: 0, faturamento: 0, base: 0, bonus: 0 },
        sdr:    { vendas: 0, faturamento: 0, base: 0, bonus: 0 },
      });
    }
    return byUser.get(user.id) ?? null;
  };

  // Semeia com usuários conhecidos (aparecem no ranking mesmo sem venda).
  seedUsers.forEach(u => ensure(u, u.role === 'closer' ? 'Closer' : 'SDR'));

  for (const c of commissions) {
    const fat = Number(c.faturamento) || 0;

    if (c.closer?.id) {
      const e = ensure(c.closer, 'Closer');
      if (e) {
        e.closer.vendas += 1;
        e.closer.faturamento += fat;
        e.closer.base += Number(c.closer_commission) || 0;
      }
    }

    // Lado SDR só quando há SDR distinto (não closer_as_sdr).
    if (!c.closer_as_sdr && c.sdr?.id) {
      const e = ensure(c.sdr, 'SDR');
      if (e) {
        e.sdr.vendas += 1;
        e.sdr.faturamento += fat;
        e.sdr.base += Number(c.sdr_commission) || 0;
      }
    }
  }

  const out: PersonTotals[] = [];
  for (const e of byUser.values()) {
    // Bônus POR PESSOA, por lado — nunca sobre o pool do time.
    e.closer.bonus = getBonusForFaturamento(e.closer.faturamento, safeRates.closer_bonus);
    e.sdr.bonus    = getBonusForFaturamento(e.sdr.faturamento,    safeRates.sdr_bonus);

    const comissaoBase = e.closer.base + e.sdr.base;
    const bonus        = e.closer.bonus + e.sdr.bonus;

    out.push({
      ...e,
      vendas:      e.closer.vendas + e.sdr.vendas,
      faturamento: e.closer.faturamento + e.sdr.faturamento,
      comissaoBase,
      bonus,
      comissao:    comissaoBase + bonus,   // total (base + bônus) — usado no ranking
      total:       comissaoBase + bonus,
    });
  }
  return out;
};

export interface ConsolidatedTotals {
  comissaoBase: number;
  bonus: number;
  total: number;
}

// Soma um array de pessoas (saída de computePersonTotals) para os cards
// consolidados. Σ por pessoa — o bônus consolidado é a soma dos bônus
// individuais, não uma faixa única sobre o faturamento agregado.
export const sumPersonTotals = (people: PersonTotals[] = []): ConsolidatedTotals => people.reduce(
  (acc, p) => ({
    comissaoBase: acc.comissaoBase + (Number(p.comissaoBase) || 0),
    bonus:        acc.bonus        + (Number(p.bonus) || 0),
    total:        acc.total        + (Number(p.total) || 0),
  }),
  { comissaoBase: 0, bonus: 0, total: 0 },
);

// Ordenação padrão do ranking: maior comissão (base+bônus) primeiro;
// empate/zerados por nome.
export const sortByComissao = (people: PersonTotals[] = []): PersonTotals[] => [...people].sort((a, b) => {
  if (b.comissao !== a.comissao) return b.comissao - a.comissao;
  return (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
});
